import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useAuth, Branch } from './AuthContext';
import { authService } from '../services/auth.service';
import { settingsService } from '../services/settings.service';

export interface BrandColors {
  primary: string;
  primaryHover: string;
  primaryLight: string;
  secondary: string;
  accent: string;
  success: string;
  warning: string;
  danger: string;
}

export type { Branch };

// Default colors (current indigo theme)
const defaultBrandColors: BrandColors = {
  primary: '#4f46e5',      // indigo-600
  primaryHover: '#4338ca', // indigo-700
  primaryLight: '#eef2ff', // indigo-50
  secondary: '#8b5cf6',    // purple-500
  accent: '#ec4899',       // pink-500
  success: '#10b981',      // green-500
  warning: '#f59e0b',      // amber-500
  danger: '#ef4444',       // red-500
};

interface RestaurantContextType {
  restaurantName: string;
  restaurantPhone: string | null;
  restaurantEmail: string | null;
  restaurantLogo: string | null;
  brandColors: BrandColors;
  currentBranch: Branch | null;
  branches: Branch[];
  orderByTable: boolean;
  selectedTable: string | null;
  setRestaurantName: (name: string) => void;
  setRestaurantLogo: (logo: string | null) => void;
  setBrandColors: (colors: BrandColors) => void;
  resetBrandColors: () => void;
  setCurrentBranch: (branch: Branch | null) => void;
  setBranches: (branches: Branch[]) => void;
  setOrderByTable: (enabled: boolean) => void;
  setSelectedTable: (tableId: string | null) => void;
}

const RestaurantContext = createContext<RestaurantContextType | undefined>(undefined);

export function RestaurantProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, activeTenantId } = useAuth();
  const lastResolvedTenantRef = useRef<string | null>(null);

  const getActiveTenantId = () => activeTenantId || user?.tenantId || user?.restaurantId || localStorage.getItem('activeTenantId') || null;

  const readTenantBranchMap = (): Record<string, Branch> => {
    const raw = localStorage.getItem('currentBranchByTenant');
    if (!raw) return {};
    try {
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  };

  const writeTenantBranch = (branch: Branch | null) => {
    const tenantId = getActiveTenantId();
    if (!tenantId) return;

    const map = readTenantBranchMap();
    if (branch) {
      map[tenantId] = branch;
    } else {
      delete map[tenantId];
    }
    localStorage.setItem('currentBranchByTenant', JSON.stringify(map));
  };

  const getSavedBranchForTenant = (): Branch | null => {
    const tenantId = getActiveTenantId();
    if (!tenantId) return null;
    const map = readTenantBranchMap();
    return map[tenantId] || null;
  };
  
  const [restaurantName, setRestaurantNameState] = useState<string>(() => {
    const saved = localStorage.getItem('restaurantName');
    return saved || 'Qrypt';
  });

  const [restaurantPhone, setRestaurantPhoneState] = useState<string | null>(() =>
    localStorage.getItem('restaurantPhone') || null
  );

  const [restaurantEmail, setRestaurantEmailState] = useState<string | null>(() =>
    localStorage.getItem('restaurantEmail') || null
  );

  const [restaurantLogo, setRestaurantLogoState] = useState<string | null>(() => {
    const saved = localStorage.getItem('restaurantLogo');
    return saved || null;
  });

  const [brandColors, setBrandColorsState] = useState<BrandColors>(() => {
    const saved = localStorage.getItem('brandColors');
    return saved ? JSON.parse(saved) : defaultBrandColors;
  });

  const [currentBranch, setCurrentBranchState] = useState<Branch | null>(() => {
    const saved = localStorage.getItem('currentBranch');
    return saved ? JSON.parse(saved) : null;
  });

  const [branches, setBranchesState] = useState<Branch[]>(() => {
    const saved = localStorage.getItem('branches');
    return saved ? JSON.parse(saved) : [];
  });

  const [orderByTable, setOrderByTableState] = useState<boolean>(() => {
    const saved = localStorage.getItem('orderByTable');
    return saved ? JSON.parse(saved) : false;
  });

  const [selectedTable, setSelectedTableState] = useState<string | null>(() => {
    const saved = localStorage.getItem('selectedTable');
    return saved || null;
  });

  useEffect(() => {
    const loadTenantSettings = async () => {
      if (!isAuthenticated || !user?.tenantId) {
        return;
      }

      try {
        const response = await settingsService.getTenantSettings();
        const tenantSettings = response.data;

        if (!tenantSettings) {
          return;
        }

        if (tenantSettings.name) {
          setRestaurantNameState(tenantSettings.name);
          localStorage.setItem('restaurantName', tenantSettings.name);
        }

        if (tenantSettings.address !== undefined) {
          if (tenantSettings.address) {
            localStorage.setItem('restaurantAddress', tenantSettings.address);
          } else {
            localStorage.removeItem('restaurantAddress');
          }

          // Keep branch location display aligned with Settings > Address
          setBranchesState((prev) => {
            const next = prev.map((branch) =>
              branch.isMain ? { ...branch, address: tenantSettings.address || undefined } : branch,
            );
            localStorage.setItem('branches', JSON.stringify(next));
            return next;
          });

          setCurrentBranchState((prev) => {
            if (!prev) return prev;
            const next = { ...prev, address: tenantSettings.address || undefined };
            localStorage.setItem('currentBranch', JSON.stringify(next));
            return next;
          });
        }

        if (tenantSettings.logo !== undefined) {
          setRestaurantLogoState(tenantSettings.logo || null);
          if (tenantSettings.logo) {
            localStorage.setItem('restaurantLogo', tenantSettings.logo);
          } else {
            localStorage.removeItem('restaurantLogo');
          }
        }

        setOrderByTableState(Boolean(tenantSettings.orderByTable));
        localStorage.setItem('orderByTable', JSON.stringify(Boolean(tenantSettings.orderByTable)));

        if (tenantSettings.phone !== undefined) {
          if (tenantSettings.phone) {
            setRestaurantPhoneState(tenantSettings.phone);
            localStorage.setItem('restaurantPhone', tenantSettings.phone);
          } else {
            setRestaurantPhoneState(null);
            localStorage.removeItem('restaurantPhone');
          }
        }

        if (tenantSettings.email !== undefined) {
          if (tenantSettings.email) {
            setRestaurantEmailState(tenantSettings.email);
            localStorage.setItem('restaurantEmail', tenantSettings.email);
          } else {
            setRestaurantEmailState(null);
            localStorage.removeItem('restaurantEmail');
          }
        }

        if (
          tenantSettings.primaryColor ||
          tenantSettings.primaryHoverColor ||
          tenantSettings.primaryLightColor ||
          tenantSettings.secondaryColor ||
          tenantSettings.accentColor ||
          tenantSettings.successColor ||
          tenantSettings.warningColor ||
          tenantSettings.dangerColor
        ) {
          const nextColors: BrandColors = {
            ...defaultBrandColors,
            primary: tenantSettings.primaryColor || defaultBrandColors.primary,
            primaryHover: tenantSettings.primaryHoverColor || defaultBrandColors.primaryHover,
            primaryLight: tenantSettings.primaryLightColor || defaultBrandColors.primaryLight,
            secondary: tenantSettings.secondaryColor || defaultBrandColors.secondary,
            accent: tenantSettings.accentColor || defaultBrandColors.accent,
            success: tenantSettings.successColor || defaultBrandColors.success,
            warning: tenantSettings.warningColor || defaultBrandColors.warning,
            danger: tenantSettings.dangerColor || defaultBrandColors.danger,
          };

          setBrandColorsState(nextColors);
          localStorage.setItem('brandColors', JSON.stringify(nextColors));
        }
      } catch (error) {
        console.error('Failed to load tenant settings into restaurant context:', error);
      }
    };

    loadTenantSettings();
  }, [isAuthenticated, activeTenantId, user?.tenantId]);

  // Load branches for the active tenant and reconcile the selected branch.
  useEffect(() => {
    const reconcileBranches = (nextBranches: Branch[], preferMainBranch = false) => {
      setBranchesState(nextBranches);
      localStorage.setItem('branches', JSON.stringify(nextBranches));

      const savedBranch = localStorage.getItem('currentBranch');
      const currentSavedBranch = savedBranch ? JSON.parse(savedBranch) : null;
      const tenantSavedBranch = getSavedBranchForTenant();
      const mainBranch = nextBranches.find((branch) => branch.isMain) || null;
      const resolvedBranch = preferMainBranch
        ? mainBranch || nextBranches[0] || null
        : nextBranches.find((branch) => branch.id === tenantSavedBranch?.id) ||
          (isAuthenticated
            ? null
            : nextBranches.find((branch) => branch.id === currentSavedBranch?.id)) ||
          mainBranch ||
          nextBranches[0] ||
          null;

      setCurrentBranchState(resolvedBranch);
      if (resolvedBranch) {
        localStorage.setItem('currentBranch', JSON.stringify(resolvedBranch));
        writeTenantBranch(resolvedBranch);
      } else {
        localStorage.removeItem('currentBranch');
        writeTenantBranch(null);
      }
    };

    const loadBranches = async () => {
      if (!isAuthenticated) {
        setBranchesState([]);
        setCurrentBranchState(null);
        localStorage.removeItem('branches');
        localStorage.removeItem('currentBranch');

        setRestaurantNameState('Qrypt');
        setRestaurantLogoState(null);
        setBrandColorsState(defaultBrandColors);
        setOrderByTableState(false);

        localStorage.removeItem('restaurantName');
        localStorage.removeItem('restaurantAddress');
        localStorage.removeItem('restaurantPhone');
        localStorage.removeItem('restaurantEmail');
        localStorage.removeItem('restaurantLogo');
        localStorage.removeItem('brandColors');
        localStorage.removeItem('orderByTable');
        return;
      }

      // Always clear stale branch state before loading active-tenant branches.
      // This prevents previous-tenant branches from lingering in the TopBar
      // if the new request is slow or fails.
      setBranchesState([]);
      setCurrentBranchState(null);
      localStorage.removeItem('branches');
      localStorage.removeItem('currentBranch');

      try {
        const activeTenantId = getActiveTenantId();
        const tenantJustChanged =
          !!activeTenantId &&
          !!lastResolvedTenantRef.current &&
          lastResolvedTenantRef.current !== activeTenantId;

        const response = await authService.getSessionContext();
        const payload: any = response.data;
        const rows = Array.isArray(payload?.branches)
          ? payload.branches
          : Array.isArray(payload?.data?.branches)
            ? payload.data.branches
            : [];

        const nextBranches: Branch[] = rows.map((branch: any) => ({
          id: branch.id,
          restaurantId: branch.tenantId,
          name: branch.name,
          code: branch.code,
          address: branch.address || undefined,
          phone: branch.phone || undefined,
          email: branch.email || undefined,
          isMain: branch.isMain,
          isActive: branch.isActive,
          manager: branch.manager || undefined,
          openingHours: branch.openingHours,
          settings: branch.settings,
        }));

        if (nextBranches.length > 0) {
          reconcileBranches(nextBranches, tenantJustChanged);
          if (activeTenantId) {
            lastResolvedTenantRef.current = activeTenantId;
          }
          return;
        }
      } catch (error) {
        console.error('Failed to load branches into restaurant context:', error);
      }

      // Keep state empty on failure/no-branches for authenticated users.
      // Never fall back to stale or mock branches in authenticated tenant mode.
      setBranchesState([]);
      setCurrentBranchState(null);
      localStorage.removeItem('branches');
      localStorage.removeItem('currentBranch');
    };

    loadBranches();
  }, [isAuthenticated, activeTenantId, user?.tenantId]);

  const setRestaurantName = (name: string) => {
    setRestaurantNameState(name);
    localStorage.setItem('restaurantName', name);
  };

  const setRestaurantLogo = (logo: string | null) => {
    setRestaurantLogoState(logo);
    if (logo) {
      localStorage.setItem('restaurantLogo', logo);
    } else {
      localStorage.removeItem('restaurantLogo');
    }
  };

  const setBrandColors = (colors: BrandColors) => {
    setBrandColorsState(colors);
    localStorage.setItem('brandColors', JSON.stringify(colors));
  };

  const resetBrandColors = () => {
    setBrandColorsState(defaultBrandColors);
    localStorage.removeItem('brandColors');
  };

  const setCurrentBranch = (branch: Branch | null) => {
    setCurrentBranchState(branch);
    if (branch) {
      localStorage.setItem('currentBranch', JSON.stringify(branch));
      writeTenantBranch(branch);
    } else {
      localStorage.removeItem('currentBranch');
      writeTenantBranch(null);
    }
  };

  const setBranches = (branches: Branch[]) => {
    setBranchesState(branches);
    localStorage.setItem('branches', JSON.stringify(branches));
  };

  const setOrderByTable = (enabled: boolean) => {
    setOrderByTableState(enabled);
    localStorage.setItem('orderByTable', JSON.stringify(enabled));
  };

  const setSelectedTable = (tableId: string | null) => {
    setSelectedTableState(tableId);
    if (tableId) {
      localStorage.setItem('selectedTable', tableId);
    } else {
      localStorage.removeItem('selectedTable');
    }
  };

  // Apply brand colors as CSS variables
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', brandColors.primary);
    root.style.setProperty('--color-primary-hover', brandColors.primaryHover);
    root.style.setProperty('--color-primary-light', brandColors.primaryLight);
    root.style.setProperty('--color-secondary', brandColors.secondary);
    root.style.setProperty('--color-accent', brandColors.accent);
    root.style.setProperty('--color-success', brandColors.success);
    root.style.setProperty('--color-warning', brandColors.warning);
    root.style.setProperty('--color-danger', brandColors.danger);
  }, [brandColors]);

  return (
    <RestaurantContext.Provider
      value={{
        restaurantName,
        restaurantPhone,
        restaurantEmail,
        restaurantLogo,
        brandColors,
        currentBranch,
        branches,
        orderByTable,
        selectedTable,
        setRestaurantName,
        setRestaurantLogo,
        setBrandColors,
        resetBrandColors,
        setCurrentBranch,
        setBranches,
        setOrderByTable,
        setSelectedTable,
      }}
    >
      {children}
    </RestaurantContext.Provider>
  );
}

export function useRestaurant() {
  const context = useContext(RestaurantContext);
  if (context === undefined) {
    throw new Error('useRestaurant must be used within a RestaurantProvider');
  }
  return context;
}